import { useMutation, useQuery } from '@apollo/client';
import { CheckIcon } from '@heroicons/react/24/solid';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  GetQuizAttemptReviewQuery,
  GetTestAttemptScoresQuery,
  QuestionAnswerType,
} from '../../../apollo/__generated__/graphql.ts';
import HighlightProvier from '../../../contexts/highlight-provider/index.tsx';
import { useHighlight } from '../../../contexts/highlight-provider/useHighlight.ts';
import { useQuestionHighlightVisibility } from '../../../contexts/highlight-provider/use-question-highlight-visibility.ts';
import { StoredHighlightDataT } from '../../../contexts/highlight-provider/types.ts';
import {
  GET_QUESTION_LOG_STATUSES,
  GetQuestionLogStatusesQuery,
  GetQuestionLogStatusesQueryVariables,
  MARK_QUESTION_LOG_OCCURRENCE_REVIEWED,
  MARK_QUESTION_LOG_OCCURRENCE_UNREVIEWED,
  MARK_QUESTION_LOG_REVIEWED,
  MARK_QUESTION_LOG_UNREVIEWED,
  QUESTION_LOG_REVIEW_STATUS,
  QuestionLogSourceType,
  QuestionLogEntry,
  SAVE_QUESTION_LOG_REVIEW_ANNOTATIONS,
  TOGGLE_QUESTION_LOG_MARKED_FOR_REVIEW,
  TOGGLE_QUESTION_LOG_STAR,
} from '../../../apollo/question-log.ts';
import { capitalizeWords } from '../../../utils/capitalize-words.ts';
import { ArrayElement } from '../../../utils/types.ts';
import Button from '../../common/button.tsx';
import Modal from '../../common/modal.tsx';
import { QuestionLogStarButton } from '../../question-log/question-log-star-button.tsx';
import { useExplanation } from '../questions-hooks.tsx';
import QuestionContent, { ICompletedData } from './question-content.tsx';
import QuestionReportForm from './question-report-form.tsx';
import { ReviewTools } from './review-tools.tsx';
import ReferenceModal from './reference-modal.tsx';
import { ReviewModeHeader } from './review-mode-header.tsx';
import { useReviewAnswerDefault } from './use-review-answer-default.ts';
import { dispatchQuestionLogStatusPatched } from '../../../utils/question-log-status-sync.ts';

type QuestionLogStatusEntry = Omit<
  Pick<
    QuestionLogEntry,
    | 'occurrenceId'
    | 'questionEntryId'
    | 'isStarredOnly'
    | 'questionId'
    | 'reviewStatus'
    | 'reviewedAt'
    | 'starred'
    | 'starredAt'
    | 'markedForReview'
    | 'markedForReviewAt'
    | 'wrongCount'
    | 'itemKey'
    | 'itemType'
    | 'reviewAnnotations'
  >,
  never
> & {
  occurrenceId?: number | null;
  questionEntryId?: number;
  isStarredOnly?: boolean;
  reviewAnnotations?: StoredHighlightDataT[] | null;
};

type QuestionLogMutableStatusField =
  | 'reviewStatus'
  | 'starred'
  | 'markedForReview';

interface IReviewQuestionContentProps {
  question:
    | ArrayElement<
        GetQuizAttemptReviewQuery['quizAttempt']['getAttempt']['quiz']['quizQuestions']
      >['question']
    | GetTestAttemptScoresQuery['testAttempt']['getAttempt']['test']['sections'][0]['modules'][0]['questions'][0]['question'];
  order?: number;
  answer?: string | number;
  completedData: ICompletedData;
  isMath: boolean;
  isExplanation: boolean;
  setIsExplanation: (value: boolean) => void;
  isAnswerVisible: boolean;
  isCalc: boolean;
  setIsCalc: Dispatch<SetStateAction<boolean>>;
  isMark: boolean;
  setMark: (mark: boolean) => void;
  reviewStartsRevealed: boolean;
  onSetAnswerVisible: (value: boolean) => void;
}

const QuestionReviewQuestionContent = ({
  question,
  order,
  answer,
  completedData,
  isMath,
  isExplanation,
  setIsExplanation,
  isAnswerVisible,
  isCalc,
  setIsCalc,
  isMark,
  setMark,
  reviewStartsRevealed,
  onSetAnswerVisible,
}: IReviewQuestionContentProps) => {
  const { highlightStickersToggle } = useHighlight();
  const { hasQuestionStickers, toggleQuestionNotesVisible } =
    useQuestionHighlightVisibility(question?.id);

  return (
    <QuestionContent
      questionId={question?.id}
      questionNumber={order}
      prompt={question?.prompt || ''}
      content={question?.content}
      isMark={isMark}
      setMark={setMark}
      choicesData={
        question?.answerType === QuestionAnswerType.MultipleChoice
          ? question?.choices
          : undefined
      }
      isPreviewMode={true}
      explanation={question?.explanation}
      isExplanation={isExplanation}
      setIsExplanation={setIsExplanation}
      answer={answer}
      isMath={isMath}
      completedData={completedData}
      isAnswerVisible={isAnswerVisible}
      setIsAnswerVisible={onSetAnswerVisible}
      stickyBottomActions
      allowReviewInteractions
      isShowHighLights
      highlightStickersToggle={highlightStickersToggle}
      hasQuestionStickers={hasQuestionStickers}
      onToggleNoteVisibility={toggleQuestionNotesVisible}
      isCalc={isCalc}
      setIsCalc={setIsCalc}
      reviewStartsRevealed={reviewStartsRevealed}
    />
  );
};

interface IProps {
  isOpen: boolean;
  onClose: () => void;
  question?:
    | ArrayElement<
        GetQuizAttemptReviewQuery['quizAttempt']['getAttempt']['quiz']['quizQuestions']
      >['question']
    | GetTestAttemptScoresQuery['testAttempt']['getAttempt']['test']['sections'][0]['modules'][0]['questions'][0]['question'];
  order?: number;
  answer?: string | number;
  completedData: ICompletedData;
  questionIds?: number[];
  onNext?: () => void;
  onPrev?: () => void;
  nextDisabled?: boolean;
  prevDisabled?: boolean;
  sourceType?: QuestionLogSourceType;
  attemptId?: number;
  sourceName?: string;
}

const createDefaultQuestionLogStatus = (
  questionId: number,
): QuestionLogStatusEntry => ({
  questionId,
  reviewStatus: QUESTION_LOG_REVIEW_STATUS.Unreviewed,
  reviewedAt: null,
  starred: false,
  starredAt: null,
  markedForReview: false,
  markedForReviewAt: null,
  wrongCount: 0,
  itemKey: `question:${questionId}`,
  itemType: 'EntryOnly',
  occurrenceId: null,
  questionEntryId: 0,
  isStarredOnly: false,
  reviewAnnotations: [],
});

export const QuestionReviewModal = ({
  isOpen,
  onClose,
  question,
  order,
  answer,
  onNext,
  onPrev,
  completedData,
  questionIds,
  nextDisabled,
  prevDisabled,
  sourceType,
  attemptId,
  sourceName,
}: IProps) => {
  const navigationButtonClass =
    '!border-[#D7E3F2] !bg-[#F7FAFE] !text-dark-blue transition-colors hover:!border-[#C5D6EC] hover:!bg-[#EEF4FB] hover:!text-dark-blue';
  const { isExplanation, setIsExplanation } = useExplanation();
  const [isAnswerVisible, setIsAnswerVisible] = useState(false);
  const [isCalc, setIsCalc] = useState(false);
  const [isReferenceOpen, setIsReferenceOpen] = useState(false);
  const [isReportOpen, setIsReportOpen] = useState(false);
  const [reviewAnnotations, setReviewAnnotations] = useState<
    StoredHighlightDataT[]
  >([]);
  const [questionLogStatuses, setQuestionLogStatuses] = useState<
    Record<number, QuestionLogStatusEntry>
  >({});
  const [answerVisibilityOverrides, setAnswerVisibilityOverrides] = useState<
    Record<string, boolean>
  >({});
  const [isAdvancedInfoOpen, setIsAdvancedInfoOpen] = useState(false);
  const mutationVersionRef = useRef<Record<string, number>>({});
  const localQuestionLogFieldChangesRef = useRef<
    Record<number, Partial<Record<QuestionLogMutableStatusField, true>>>
  >({});
  const { showAnswersByDefault, setShowAnswersByDefault } =
    useReviewAnswerDefault();

  const normalizedQuestionIds = useMemo(
    () =>
      Array.from(
        new Set(
          (questionIds ?? []).filter(
            (questionId): questionId is number =>
              Number.isInteger(questionId) && questionId > 0,
          ),
        ),
      ),
    [questionIds],
  );

  const { data: questionLogStatusData } = useQuery<
    GetQuestionLogStatusesQuery,
    GetQuestionLogStatusesQueryVariables
  >(GET_QUESTION_LOG_STATUSES, {
    variables: {
      questionIds: normalizedQuestionIds,
      sourceType,
      attemptId,
    },
    skip: !normalizedQuestionIds.length,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    errorPolicy: 'all',
  });

  const [markReviewed] = useMutation(MARK_QUESTION_LOG_REVIEWED);
  const [markUnreviewed] = useMutation(MARK_QUESTION_LOG_UNREVIEWED);
  const [markOccurrenceReviewed] = useMutation(
    MARK_QUESTION_LOG_OCCURRENCE_REVIEWED,
  );
  const [markOccurrenceUnreviewed] = useMutation(
    MARK_QUESTION_LOG_OCCURRENCE_UNREVIEWED,
  );
  const [toggleStar] = useMutation(TOGGLE_QUESTION_LOG_STAR);
  const [toggleMarkedForReview] = useMutation(
    TOGGLE_QUESTION_LOG_MARKED_FOR_REVIEW,
  );
  const [saveReviewAnnotations] = useMutation(
    SAVE_QUESTION_LOG_REVIEW_ANNOTATIONS,
  );

  const questionIdentifier = question?.originalId ?? question?.id;
  const questionVisibilityKey =
    question?.id != null ? String(question.id) : undefined;
  const reviewStorageKey =
    question?.id != null
      ? `review-annotations:${question.id}`
      : undefined;
  const questionAnswerVisibilityOverride =
    questionVisibilityKey != null
      ? answerVisibilityOverrides[questionVisibilityKey]
      : undefined;
  const reviewStartsRevealed =
    questionAnswerVisibilityOverride ?? showAnswersByDefault;
  const reviewStartsRevealedRef = useRef(reviewStartsRevealed);

  reviewStartsRevealedRef.current = reviewStartsRevealed;

  useEffect(() => {
    const nextStatuses =
      questionLogStatusData?.questionLog?.getQuestionLogStatuses ?? [];

    if (!nextStatuses.length) {
      return;
    }

    setQuestionLogStatuses((current) => {
      const mergedStatuses = { ...current };

      nextStatuses.forEach((entry) => {
        const currentEntry = current[entry.questionId];
        const localChanges =
          localQuestionLogFieldChangesRef.current[entry.questionId];

        if (!currentEntry || !localChanges) {
          mergedStatuses[entry.questionId] = entry;
          return;
        }

        mergedStatuses[entry.questionId] = {
          ...entry,
          ...(localChanges.reviewStatus && {
            reviewStatus: currentEntry.reviewStatus,
            reviewedAt: currentEntry.reviewedAt,
          }),
          ...(localChanges.starred && {
            starred: currentEntry.starred,
            starredAt: currentEntry.starredAt,
          }),
          ...(localChanges.markedForReview && {
            markedForReview: currentEntry.markedForReview,
            markedForReviewAt: currentEntry.markedForReviewAt,
          }),
        };
      });

      return mergedStatuses;
    });
  }, [questionLogStatusData]);

  const currentQuestionLogStatus = useMemo(() => {
    if (!question?.id) {
      return undefined;
    }

    return (
      questionLogStatuses[question.id] ??
      createDefaultQuestionLogStatus(question.id)
    );
  }, [question?.id, questionLogStatuses]);

  const initialReviewAnnotations = useMemo(
    () =>
      ((currentQuestionLogStatus?.reviewAnnotations as
        | StoredHighlightDataT[]
        | null
        | undefined) ?? []),
    [currentQuestionLogStatus?.reviewAnnotations],
  );

  useEffect(() => {
    setIsExplanation(false);
    setIsAnswerVisible(reviewStartsRevealedRef.current);
    setIsCalc(false);
    setIsReferenceOpen(false);
    setIsReportOpen(false);
  }, [questionVisibilityKey, setIsExplanation]);

  useEffect(() => {
    if (!isOpen) {
      setIsReferenceOpen(false);
      setIsReportOpen(false);
    }
  }, [isOpen]);

  useEffect(() => {
    setReviewAnnotations(initialReviewAnnotations);
  }, [initialReviewAnnotations, questionVisibilityKey]);

  const onSetAnswerVisible = useCallback(
    (value: boolean) => {
      setIsAnswerVisible(value);

      if (!questionVisibilityKey) {
        return;
      }

      setAnswerVisibilityOverrides((current) => {
        if (value === showAnswersByDefault) {
          if (!(questionVisibilityKey in current)) {
            return current;
          }

          const nextOverrides = { ...current };
          delete nextOverrides[questionVisibilityKey];
          return nextOverrides;
        }

        return {
          ...current,
          [questionVisibilityKey]: value,
        };
      });
    },
    [questionVisibilityKey, showAnswersByDefault],
  );

  const onToggleShowAnswersByDefault = useCallback(
    (value: boolean) => {
      setShowAnswersByDefault(value);

      if (
        !questionVisibilityKey ||
        questionAnswerVisibilityOverride != null
      ) {
        return;
      }

      setIsAnswerVisible(value);
    },
    [
      questionAnswerVisibilityOverride,
      questionVisibilityKey,
      setShowAnswersByDefault,
    ],
  );

  const onRequestClose = useCallback(() => {
    if (isReferenceOpen) {
      setIsReferenceOpen(false);

      return;
    }

    if (isReportOpen) {
      setIsReportOpen(false);

      return;
    }

    onClose();
  }, [isReferenceOpen, isReportOpen, onClose]);

  useEffect(() => {
    if (!question?.id) {
      return;
    }

    if (
      JSON.stringify(reviewAnnotations) === JSON.stringify(initialReviewAnnotations)
    ) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      void saveReviewAnnotations({
        variables: {
          input: {
            questionId: question.id,
            reviewAnnotations,
          },
        },
      });
    }, 500);

    return () => window.clearTimeout(timeoutId);
  }, [
    initialReviewAnnotations,
    question?.id,
    reviewAnnotations,
    saveReviewAnnotations,
  ]);

  const isReviewed =
    currentQuestionLogStatus?.reviewStatus ===
    QUESTION_LOG_REVIEW_STATUS.Reviewed;

  const footerMeta = useMemo(
    () =>
      [
        question?.section?.name
          ? `Section: ${capitalizeWords(question.section.name)}`
          : null,
        question?.domain?.name
          ? `Domain: ${capitalizeWords(question.domain.name)}`
          : null,
        question?.skill?.name
          ? `Skill: ${capitalizeWords(question.skill.name)}`
          : null,
        question?.type1?.name
          ? `Qtype: ${capitalizeWords(question.type1.name)}`
          : null,
        currentQuestionLogStatus?.wrongCount != null
          ? `Attempts: ${currentQuestionLogStatus.wrongCount}`
          : null,
      ].filter((item): item is string => Boolean(item)),
    [currentQuestionLogStatus?.wrongCount, question],
  );

  const startMutationVersion = (type: string, questionId: number) => {
    const mutationKey = `${type}-${questionId}`;
    const nextVersion = (mutationVersionRef.current[mutationKey] ?? 0) + 1;

    mutationVersionRef.current[mutationKey] = nextVersion;

    return { mutationKey, version: nextVersion };
  };

  const isLatestMutationVersion = (mutationKey: string, version: number) =>
    mutationVersionRef.current[mutationKey] === version;

  const markQuestionLogFieldChanged = (
    questionId: number,
    field: QuestionLogMutableStatusField,
  ) => {
    localQuestionLogFieldChangesRef.current[questionId] = {
      ...localQuestionLogFieldChangesRef.current[questionId],
      [field]: true,
    };
  };

  const patchQuestionLogStatus = (
    questionId: number,
    updater: (current: QuestionLogStatusEntry) => QuestionLogStatusEntry,
  ) => {
    setQuestionLogStatuses((current) => ({
      ...current,
      [questionId]: updater(
        current[questionId] ?? createDefaultQuestionLogStatus(questionId),
      ),
    }));
  };

  const onToggleQuestionStar = () => {
    if (!question?.id || !currentQuestionLogStatus) {
      return;
    }

    const nextStarred = !currentQuestionLogStatus.starred;
    const previousStatus = currentQuestionLogStatus;
    const { mutationKey, version } = startMutationVersion('star', question.id);
    markQuestionLogFieldChanged(question.id, 'starred');

    patchQuestionLogStatus(question.id, (current) => ({
      ...current,
      starred: nextStarred,
      starredAt: nextStarred ? new Date().toISOString() : null,
    }));

    void (async () => {
      try {
        const result = await toggleStar({
          variables: {
            input: {
              questionId: question.id,
              starred: nextStarred,
            },
          },
        });

        if (!isLatestMutationVersion(mutationKey, version)) {
          return;
        }

        const nextEntry = result.data?.questionLog?.toggleStar;

        patchQuestionLogStatus(question.id, (current) => ({
          ...current,
          questionId: nextEntry?.questionId ?? current.questionId,
          reviewStatus: nextEntry?.reviewStatus ?? current.reviewStatus,
          reviewedAt: nextEntry?.reviewedAt ?? current.reviewedAt,
          starred: nextStarred,
          starredAt:
            nextEntry?.starredAt ??
            (nextStarred ? new Date().toISOString() : null),
          wrongCount: nextEntry?.wrongCount ?? current.wrongCount,
        }));
        dispatchQuestionLogStatusPatched({
          questionId: question.id,
          starred: nextStarred,
          starredAt:
            nextEntry?.starredAt ??
            (nextStarred ? new Date().toISOString() : null),
        });
      } catch {
        if (!isLatestMutationVersion(mutationKey, version)) {
          return;
        }

        setQuestionLogStatuses((current) => ({
          ...current,
          [question.id]: previousStatus,
        }));
      }
    })();
  };

  const onSetReviewStatus = (
    nextReviewStatus:
      | typeof QUESTION_LOG_REVIEW_STATUS.Reviewed
      | typeof QUESTION_LOG_REVIEW_STATUS.Unreviewed,
  ) => {
    if (!question?.id || !currentQuestionLogStatus) {
      return;
    }

    const previousStatus = currentQuestionLogStatus;
    const nextReviewedAt =
      nextReviewStatus === QUESTION_LOG_REVIEW_STATUS.Reviewed
        ? new Date().toISOString()
        : null;
    const { mutationKey, version } = startMutationVersion(
      'review',
      question.id,
    );
    markQuestionLogFieldChanged(question.id, 'reviewStatus');

    patchQuestionLogStatus(question.id, (current) => ({
      ...current,
      reviewStatus: nextReviewStatus,
      reviewedAt: nextReviewedAt,
    }));

    void (async () => {
      try {
        const result =
          currentQuestionLogStatus.occurrenceId
            ? nextReviewStatus === QUESTION_LOG_REVIEW_STATUS.Reviewed
              ? await markOccurrenceReviewed({
                  variables: {
                    input: {
                      occurrenceId: currentQuestionLogStatus.occurrenceId,
                    },
                  },
                })
              : await markOccurrenceUnreviewed({
                  variables: {
                    input: {
                      occurrenceId: currentQuestionLogStatus.occurrenceId,
                    },
                  },
                })
            : nextReviewStatus === QUESTION_LOG_REVIEW_STATUS.Reviewed
              ? await markReviewed({
                  variables: {
                    input: { questionId: question.id },
                  },
                })
              : await markUnreviewed({
                  variables: {
                    input: { questionId: question.id },
                  },
                });

        if (!isLatestMutationVersion(mutationKey, version)) {
          return;
        }

        const nextEntry =
          currentQuestionLogStatus.occurrenceId
            ? nextReviewStatus === QUESTION_LOG_REVIEW_STATUS.Reviewed
              ? result.data?.questionLog?.markOccurrenceReviewed
              : result.data?.questionLog?.markOccurrenceUnreviewed
            : nextReviewStatus === QUESTION_LOG_REVIEW_STATUS.Reviewed
              ? result.data?.questionLog?.markReviewed
              : result.data?.questionLog?.markUnreviewed;

        patchQuestionLogStatus(question.id, (current) => ({
          ...current,
          occurrenceId:
            currentQuestionLogStatus.occurrenceId ?? current.occurrenceId ?? null,
          questionEntryId:
            currentQuestionLogStatus.questionEntryId ??
            current.questionEntryId ??
            0,
          isStarredOnly:
            currentQuestionLogStatus.isStarredOnly ??
            current.isStarredOnly ??
            false,
          questionId: nextEntry?.questionId ?? current.questionId,
          reviewStatus: nextEntry?.reviewStatus ?? nextReviewStatus,
          reviewedAt: nextEntry?.reviewedAt ?? nextReviewedAt,
          starred: nextEntry?.starred ?? current.starred,
          starredAt: nextEntry?.starredAt ?? current.starredAt,
          wrongCount: nextEntry?.wrongCount ?? current.wrongCount,
        }));
      } catch {
        if (!isLatestMutationVersion(mutationKey, version)) {
          return;
        }

        setQuestionLogStatuses((current) => ({
          ...current,
          [question.id]: previousStatus,
        }));
      }
    })();
  };

  const onToggleMarkedQuestionForReview = (
    mark = !currentQuestionLogStatus?.markedForReview,
  ) => {
    if (!question?.id || !currentQuestionLogStatus) {
      return;
    }

    const nextMarkedForReview = mark;
    const previousStatus = currentQuestionLogStatus;
    const { mutationKey, version } = startMutationVersion(
      'markedForReview',
      question.id,
    );
    markQuestionLogFieldChanged(question.id, 'markedForReview');

    patchQuestionLogStatus(question.id, (current) => ({
      ...current,
      markedForReview: nextMarkedForReview,
      markedForReviewAt: nextMarkedForReview ? new Date().toISOString() : null,
    }));

    void (async () => {
      try {
        const result = await toggleMarkedForReview({
          variables: {
            input: {
              questionId: question.id,
              markedForReview: nextMarkedForReview,
            },
          },
        });

        if (!isLatestMutationVersion(mutationKey, version)) {
          return;
        }

        const nextEntry = result.data?.questionLog?.toggleMarkedForReview;

        patchQuestionLogStatus(question.id, (current) => ({
          ...current,
          markedForReview: nextMarkedForReview,
          markedForReviewAt: nextMarkedForReview
            ? (nextEntry?.markedForReviewAt ?? new Date().toISOString())
            : null,
        }));
        dispatchQuestionLogStatusPatched({
          questionId: question.id,
          markedForReview: nextMarkedForReview,
          markedForReviewAt: nextMarkedForReview
            ? (nextEntry?.markedForReviewAt ?? new Date().toISOString())
            : null,
        });
      } catch {
        if (!isLatestMutationVersion(mutationKey, version)) {
          return;
        }

        setQuestionLogStatuses((current) => ({
          ...current,
          [question.id]: previousStatus,
        }));
      }
    })();
  };

  return question ? (
    <HighlightProvier
      key={reviewStorageKey}
      storageKey={reviewStorageKey}
      initialData={
        initialReviewAnnotations.length ? initialReviewAnnotations : null
      }
      onChange={setReviewAnnotations}
    >
      <>
        <Modal
          title={
            questionIdentifier != null
              ? `Question Review #${questionIdentifier}`
              : 'Question Review'
          }
          onClose={onRequestClose}
          isOpen={isOpen}
          titleContent={
            <div className="ml-2 text-[13px] font-medium text-black">
              {sourceName ? `Source: ${sourceName}` : ''}
            </div>
          }
          centerContent={
            <ReviewModeHeader
              showAnswersByDefault={showAnswersByDefault}
              onToggleShowAnswersByDefault={onToggleShowAnswersByDefault}
            />
          }
          headerActions={
            <ReviewTools
              isMath={question.section?.name === 'math'}
              isCalc={isCalc}
              onToggleCalc={() => setIsCalc((prevState) => !prevState)}
              showReport
              onOpenReport={() => setIsReportOpen(true)}
              showReference
              isReferenceOpen={isReferenceOpen}
              onOpenReference={() => setIsReferenceOpen(true)}
            />
          }
          fullScreen
          headerClassName="!my-0 !-mx-4 border-b border-black/10 bg-[#e7edf7] !px-4 !py-2"
          titleClassName="text-[13px] font-semibold"
          titleRowClassName="w-full gap-3"
          panelClassName="flex h-full w-full flex-col !overflow-hidden !px-4 !pb-4 !pt-0"
          bodyClassName="min-h-0 flex flex-1 flex-col"
        >
          <div className="flex min-h-0 flex-1 flex-col">
            <div className="min-h-0 flex-1">
              <QuestionReviewQuestionContent
                question={question}
                order={order}
                answer={answer}
                completedData={completedData}
                isMath={question?.section?.name === 'math'}
                isExplanation={isExplanation}
                setIsExplanation={setIsExplanation}
                isAnswerVisible={isAnswerVisible}
                onSetAnswerVisible={onSetAnswerVisible}
                isCalc={isCalc}
                setIsCalc={setIsCalc}
                isMark={!!currentQuestionLogStatus?.markedForReview}
                setMark={onToggleMarkedQuestionForReview}
                reviewStartsRevealed={reviewStartsRevealed}
              />
            </div>

            <div className="mt-3 shrink-0 rounded-2xl border border-table-border bg-white px-4 py-3 shadow-[0_-8px_24px_rgba(15,23,42,0.06)]">
              <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
                <div className="flex items-center gap-2">
                  <QuestionLogStarButton
                    starred={!!currentQuestionLogStatus?.starred}
                    onClick={onToggleQuestionStar}
                  />
                  {!!footerMeta.length && (
                    <Button
                      outlined
                      className={navigationButtonClass}
                      onClick={() =>
                        setIsAdvancedInfoOpen((currentState) => !currentState)
                      }
                    >
                      {isAdvancedInfoOpen
                        ? 'Hide Advanced Info'
                        : 'Show Advanced Info'}
                    </Button>
                  )}
                </div>

                <div className="flex flex-wrap items-center gap-2 lg:justify-end">
                  {isReviewed ? (
                    <>
                      <div className="text-sm text-gray">
                        This question has been completed.
                      </div>
                      <div className="inline-flex items-center gap-1 rounded-full bg-[#E5F6EA] px-3 py-2 text-sm font-semibold text-[#1F7A3D]">
                        <span>Completed</span>
                        <CheckIcon className="h-4 w-4" />
                      </div>
                      <button
                        type="button"
                        className="px-2 py-2 text-sm font-medium text-gray underline-offset-2 transition-colors hover:text-dark-blue hover:underline"
                        onClick={() =>
                          onSetReviewStatus(QUESTION_LOG_REVIEW_STATUS.Unreviewed)
                        }
                      >
                        Undo
                      </button>
                    </>
                  ) : (
                    <>
                      <div className="text-sm text-gray">
                        This question is still unreviewed.
                      </div>
                      <button
                        type="button"
                        className="rounded-full bg-[#E8F0FA] px-4 py-2 text-sm font-semibold text-blue transition-colors hover:bg-[#D9E7F6]"
                        onClick={() =>
                          onSetReviewStatus(QUESTION_LOG_REVIEW_STATUS.Reviewed)
                        }
                      >
                        Mark as Reviewed
                      </button>
                    </>
                  )}

                  {onPrev && !prevDisabled && (
                    <Button
                      outlined
                      className={navigationButtonClass}
                      onClick={onPrev}
                    >
                      Previous
                    </Button>
                  )}
                  {onNext && !nextDisabled && (
                    <Button
                      outlined
                      className={navigationButtonClass}
                      onClick={onNext}
                    >
                      Next
                    </Button>
                  )}
                </div>
              </div>

              {isAdvancedInfoOpen && !!footerMeta.length && (
                <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 border-t border-black/10 pt-3 text-[13px] font-medium text-black">
                  {footerMeta.map((item) => (
                    <span key={item}>{item}</span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </Modal>
        <ReferenceModal
          isOpen={isReferenceOpen}
          onClose={() => setIsReferenceOpen(false)}
        />
        <Modal
          title={'Question Report'}
          onClose={() => setIsReportOpen(false)}
          isOpen={isReportOpen}
        >
          <QuestionReportForm
            questionId={question.id}
            close={() => setIsReportOpen(false)}
          />
        </Modal>
      </>
    </HighlightProvier>
  ) : null;
};
